import React, { useState } from 'react';
import { Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { PLATFORM_UPDATES, UPDATE_CATEGORIES } from '@/data/platformUpdates';
import { useClient } from '@/contexts/ClientContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sparkles, TrendingUp, Wrench, Shield, Calendar } from 'lucide-react';

const ICON_MAP = {
  feature: Sparkles,
  enhancement: TrendingUp,
  fix: Wrench,
  security: Shield
};

export default function UpdatesNotification() {
  const [isOpen, setIsOpen] = useState(false);
  const { lastSeenUpdate, markUpdatesAsSeen } = useClient();
  
  // Count unseen updates
  const unseenCount = PLATFORM_UPDATES.filter(
    update => update.date > lastSeenUpdate
  ).length;
  
  const handleOpen = () => {
    setIsOpen(true);
    // Mark all as seen when opened
    if (PLATFORM_UPDATES.length > 0) {
      markUpdatesAsSeen(PLATFORM_UPDATES[0].date);
    }
  };
  
  // Group updates by category
  const groupedUpdates = PLATFORM_UPDATES.reduce((acc, update) => {
    if (!acc[update.category]) {
      acc[update.category] = [];
    }
    acc[update.category].push(update);
    return acc;
  }, {});

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={handleOpen}
        title="What's New"
      >
        <Sparkles className="w-5 h-5 text-primary" />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-xs font-bold text-white animate-pulse">
            {unseenCount}
          </span>
        )}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="glass-strong max-w-3xl max-h-[85vh] overflow-hidden flex flex-col border-primary/20">
          <DialogHeader className="pb-4 border-b border-border/30">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <span className="text-gradient-primary">What's New in Corvana</span>
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-2 ml-[52px]">
              Latest features and improvements
            </p>
          </DialogHeader>

          <Tabs defaultValue="all" className="flex-1 overflow-hidden flex flex-col mt-4">
            <div className="overflow-x-auto pb-2 scrollbar-hide">
              <TabsList className="glass border-border/50 inline-flex w-auto">
                <TabsTrigger value="all" className="whitespace-nowrap px-4 py-2 text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
                  All ({PLATFORM_UPDATES.length})
                </TabsTrigger>
                {Object.keys(groupedUpdates).map(category => (
                  <TabsTrigger key={category} value={category} className="whitespace-nowrap px-4 py-2 text-xs data-[state=active]:bg-primary/20 data-[state=active]:text-primary">
                    {category.split(' ')[0]} ({groupedUpdates[category].length})
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto mt-3 pr-2 space-y-3">
              <TabsContent value="all" className="space-y-3 mt-0">
                {PLATFORM_UPDATES.map((update) => {
                  const TypeIcon = ICON_MAP[update.type];
                  const categoryStyle = UPDATE_CATEGORIES[update.type];
                  const isNew = update.date > lastSeenUpdate;
                  
                  return (
                    <Card key={update.id} className={`glass-card border-border/30 hover:border-primary/30 transition-all ${isNew ? 'border-primary/40 bg-primary/5' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xl">{update.icon}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3 mb-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-sm">{update.title}</h3>
                                {isNew && (
                                  <Badge className="bg-success/20 text-success border-success/30 text-[10px] px-1.5 py-0">
                                    NEW
                                  </Badge>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <Badge className={`${categoryStyle.color} text-[10px] px-2 py-0.5`}>
                                  <TypeIcon className="w-2.5 h-2.5 mr-1" />
                                  {categoryStyle.label}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">{update.date}</span>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">{update.description}</p>
                            
                            {update.details && update.details.length > 0 && (
                              <div className="mt-2.5 pt-2.5 border-t border-border/20">
                                <ul className="space-y-1.5">
                                  {update.details.map((detail, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                                      <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
                                      <span className="leading-snug">{detail}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </TabsContent>

              {Object.entries(groupedUpdates).map(([category, updates]) => (
                <TabsContent key={category} value={category} className="space-y-3 mt-0">
                  {updates.map((update) => {
                    const TypeIcon = ICON_MAP[update.type];
                    const categoryStyle = UPDATE_CATEGORIES[update.type];
                    const isNew = update.date > lastSeenUpdate;
                    
                    return (
                      <Card key={update.id} className={`glass-card border-border/30 hover:border-primary/30 transition-all ${isNew ? 'border-primary/40 bg-primary/5' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xl">{update.icon}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3 mb-1.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-sm">{update.title}</h3>
                                  {isNew && (
                                    <Badge className="bg-success/20 text-success border-success/30 text-[10px] px-1.5 py-0">
                                      NEW
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-[10px] text-muted-foreground whitespace-nowrap flex-shrink-0">{update.date}</span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">{update.description}</p>
                              
                              {update.details && update.details.length > 0 && (
                                <div className="mt-2.5 pt-2.5 border-t border-border/20">
                                  <ul className="space-y-1.5">
                                    {update.details.map((detail, idx) => (
                                      <li key={idx} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                                        <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
                                        <span className="leading-snug">{detail}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
